import type { Question } from './types';
import type { PurchaseConversion } from './ads';
import { addPremium, applyExplains, hasPremium } from './data';
import { PRECIO_ASIENTO_FLOTA, PRECIO_INDIVIDUAL } from './precio';

const KEY_STORAGE = 'permiso-vtc.licencia';
const ADMIN_STORAGE = 'permiso-vtc.flota';

/** Promoción temporal del acceso individual; la calcula y caduca el servidor. */
export interface Promo {
  activa: boolean;
  porcentaje: number;
  hasta: string | null;
  precio: number;
}

export interface Config {
  pagoActivo: boolean;
  premiumDisponible: number;
  oficialesConCuenta?: number;
  precioIndividual: number;
  precioAsientoFlota: number;
  promo?: Promo;
  /** Si el simulacro cronometrado de 60 preguntas se puede entregar sin licencia. */
  simulacroLibre?: boolean;
  registroActivo?: boolean;
  webhookActivo?: boolean;
}

// Estos valores solo se ven en el parpadeo anterior a que responda /api/config.
// Salen de `precio.ts` para que ese parpadeo no muestre un precio viejo, que es
// justo lo que pasaba cuando estaban escritos a mano.
const SIN_PROMO: Promo = { activa: false, porcentaje: 0, hasta: null, precio: PRECIO_INDIVIDUAL };

let config: Config = {
  pagoActivo: false,
  premiumDisponible: 581,
  precioIndividual: PRECIO_INDIVIDUAL,
  precioAsientoFlota: PRECIO_ASIENTO_FLOTA,
  promo: SIN_PROMO,
  // Igual que el valor por defecto del servidor. Si aquí fuera `true` o
  // `undefined`, el simulacro se pintaría durante el instante que tarda
  // /api/config en responder y el muro aparecería después, de golpe.
  simulacroLibre: false,
};

/** Precio que se va a cobrar de verdad por el acceso individual. */
export function precioIndividualVigente(c: Config): number {
  return c.promo?.activa ? c.promo.precio : c.precioIndividual;
}

/** «quedan 3 días», «quedan 6 horas»… para dar urgencia sin mentir. */
export function cuentaAtras(hasta: string | null): string | null {
  if (!hasta) return null;
  const restante = Date.parse(hasta) - Date.now();
  if (!Number.isFinite(restante) || restante <= 0) return null;
  const horas = Math.floor(restante / 3_600_000);
  if (horas < 1) return 'menos de una hora';
  if (horas < 24) return `${horas} ${horas === 1 ? 'hora' : 'horas'}`;
  const dias = Math.round(horas / 24);
  return `${dias} ${dias === 1 ? 'día' : 'días'}`;
}

export const getConfig = (): Config => config;
export const isUnlocked = (): boolean => hasPremium();

export const getLicense = (): string | null => localStorage.getItem(KEY_STORAGE);
export const getAdminKey = (): string | null => localStorage.getItem(ADMIN_STORAGE);

// Reexportado desde `precio.ts` para no tener dos formateadores de euros que
// puedan divergir. Los consumidores siguen importándolo de aquí.
export { euros } from './precio';

export async function loadConfig(): Promise<Config> {
  try {
    const res = await fetch('/api/config');
    if (res.ok) config = await res.json();
  } catch {
    /* sin servidor: se mantienen los valores por defecto */
  }
  return config;
}

/**
 * Explicaciones de muestra de las oficiales. Se piden al arrancar, sin licencia:
 * son el anzuelo del muro de pago, así que el usuario tiene que verlas antes de
 * decidir si paga.
 */
export async function loadFreeExplains(): Promise<number> {
  try {
    const res = await fetch('/api/explains');
    if (!res.ok) return 0;
    const { explains } = (await res.json()) as { explains: Record<string, string> };
    return applyExplains(explains);
  } catch {
    return 0;
  }
}

/** Descarga el banco de pago con la licencia guardada. Devuelve cuántas añadió. */
export async function unlockWith(key: string): Promise<number> {
  const res = await fetch('/api/questions/premium', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  });
  if (!res.ok) {
    localStorage.removeItem(KEY_STORAGE);
    throw new Error('Licencia no válida');
  }

  const { questions, explains, licencia } = (await res.json()) as {
    questions: Question[];
    explains?: Record<string, string>;
    licencia?: { key: string };
  };
  const resolvedKey = licencia?.key ?? key.trim().toUpperCase();
  if (resolvedKey) localStorage.setItem(KEY_STORAGE, resolvedKey);
  if (explains) applyExplains(explains);
  return addPremium(questions);
}

/** Al arrancar, restaura el acceso si ya había licencia. */
export async function restore(): Promise<boolean> {
  const key = getLicense();
  if (!key) return false;
  try {
    await unlockWith(key);
    return true;
  } catch {
    return false;
  }
}

export async function startCheckout(seats: number): Promise<void> {
  const res = await fetch('/api/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      seats,
      consentimiento: { aceptado: true, version: 'checkout-2026-07-27' },
    }),
  });
  if (!res.ok) throw new Error('No se pudo iniciar el pago');
  const { url } = (await res.json()) as { url: string };
  window.location.href = url;
}

export interface Purchase {
  keys: string[];
  adminKey: string | null;
  yaEmitidas?: boolean;
  /** Importe verificado contra Stripe, para la conversión de Google Ads. */
  conversion: PurchaseConversion;
}

export type PurchaseStatus = Purchase | { pendiente: true; mensaje?: string };

/** Canjea la sesión de Stripe tras el redirect de vuelta. */
export async function redeem(sessionId: string): Promise<PurchaseStatus> {
  const res = await fetch(`/api/checkout/verify?session_id=${encodeURIComponent(sessionId)}`);
  if (!res.ok) throw new Error('No se pudo verificar la compra');

  const purchase = (await res.json()) as PurchaseStatus;
  if ('pendiente' in purchase) return purchase;
  if (purchase.adminKey) localStorage.setItem(ADMIN_STORAGE, purchase.adminKey);
  if (purchase.keys.length) await unlockWith(purchase.keys[0]);
  return purchase;
}
